import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { Link } from 'react-router-dom';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Campo } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import Tarefa from 'Interface/Tarefas';
import { useAppSelector } from 'Store';
import {
  useEditarTarefaMutation,
  useRemoverTarefaMutation,
} from 'View/Service/Default/Tarefas';

interface Props {
  dados: Tarefa;
  acoes?: boolean;
}

const Lista = ({ dados }: Props): React.ReactElement => {
  const toast = useToast();
  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);
  const [modal, setModal] = useState<boolean>(false);
  const [editarTarefa] = useEditarTarefaMutation();
  const [removerTarefa] = useRemoverTarefaMutation();

  const validar = async (): Promise<void> => {
    try {
      await editarTarefa({ id: dados.id, validado: 1 }).unwrap();

      toast({
        conteudo: 'Validado com sucesso!',
        tema: 'success',
      });
    } catch (erro) {
      toast({
        titulo: 'Erro ao validar',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  const editar = async (dados: Tarefa): Promise<void> => {
    try {
      await editarTarefa({ ...dados, id: dados.id }).unwrap();

      toast({
        conteudo: 'Editado com sucesso!',
        tema: 'success',
      });
    } catch (erro) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  const remover = async (): Promise<void> => {
    try {
      await removerTarefa({ id: dados.id }).unwrap();

      toast({
        conteudo: 'Removido com sucesso!',
        tema: 'success',
      });
    } catch (erro) {
      toast({
        titulo: 'Erro ao remover',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  const usuarioTarefa = dados.atendente;

  const atrasado = useMemo((): boolean => {
    if (dados.prazo.data() < DateTime.now() && dados.status == 0) {
      return true;
    }

    return false;
  }, [dados.prazo, dados.status]);

  const className = useMemo((): string => {
    let className = 'bg-success';
    if (dados.progresso <= 25) {
      className = 'bg-danger';
    } else if (dados.progresso > 25 && dados.progresso <= 75) {
      className = 'bg-warning';
    }

    return className;
  }, [dados.progresso]);

  return (
    <>
      <div className="d-flex border-bottom align-items-center py-2 px-3 gap-3">
        <Link to={`/admin/tarefas/editar/${dados.id}`}>
          <div className="position-relative">
            <Imagem
              src={usuarioTarefa?.imagem}
              alt={usuarioTarefa?.nome}
              className="rounded-circle object-fit-cover"
              width="80"
              height="80"
            />
            {atrasado && (
              <Icone
                nome="exclamation-circle-fill"
                className="text-danger position-absolute top-0 start-100 pt-3 pe-3 translate-middle fs-4"
              />
            )}
          </div>
        </Link>
        <div className="fs-5 text-truncate flex-grow-1">
          <Link
            to={`/admin/tarefas/editar/${dados.id}`}
            className="text-decoration-none"
          >
            {dados.titulo}
          </Link>
          <small className="d-block fs-6 text-muted text-truncate">
            <Icone nome="person-fill" className="me-2" />
            {usuarioTarefa?.nome}
          </small>
        </div>
        <BrowserView className="d-flex align-items-center justify-content-end gap-5">
          <div
            className="fs-6 text-end text-nowrap cursor-pointer"
            onClick={() => {
              if (
                dados.status == 0 &&
                (usuario?.permissao == 'super' ||
                  usuario?.id == dados.proprietario_id)
              ) {
                setModal(true);
              }
            }}
          >
            {dados.prazo.data().toLocaleString(DateTime.DATETIME_SHORT)}
            <small className="d-block fs-6 text-muted">
              <Icone nome="calendar" className="me-2" />
              Prazo
            </small>
          </div>
          <div className="fs-6 text-end text-nowrap">
            {dados.criado.data().toLocaleString(DateTime.DATETIME_SHORT)}
            <small className="d-block fs-6 text-muted">
              <Icone nome="calendar" className="me-2" />
              Criado em
            </small>
          </div>
          <div className="fs-5 text-end d-flex gap-2 align-items-center">
            {dados.status ? (
              <span className="badge text-bg-success">Finalizada</span>
            ) : (
              <span className="badge text-bg-warning">Aberta</span>
            )}
            {dados.status == 1 &&
              dados.validado == 0 &&
              usuario &&
              (['super'].includes(usuario.permissao) ||
                usuario.id == dados.proprietario_id) && (
                <Botao
                  icone="check-lg"
                  tema="success"
                  confirmacao="Você tem certeza que deseja validar essa tarefa?"
                  onClick={validar}
                />
              )}
            {usuario &&
              (['super'].includes(usuario.permissao) ||
                usuario.id == dados.proprietario_id) && (
                <Botao
                  icone="trash"
                  tema="danger"
                  confirmacao="Você tem certeza que deseja remover essa tarefa? Essa ação é irreversivel e tudo será perdido."
                  onClick={remover}
                />
              )}
          </div>
        </BrowserView>
      </div>
      {dados.progresso > 0 && dados.progresso < 100 && (
        <div className="progress rounded-0" style={{ height: 15 }}>
          <div
            className={`progress-bar ${className}`}
            style={{ width: `${dados.progresso}%` }}
          >
            {dados.progresso}%
          </div>
        </div>
      )}
      <Modal exibir={modal} aoFechar={setModal} titulo="Alterar prazo">
        <Form onSubmit={editar} inicial={dados}>
          <Campo
            type="datetime-local"
            name="prazo"
            rotulo="Prazo para conclusão"
          />
          <div className="d-flex gap-2 justify-content-end">
            <Botao type="submit">Enviar</Botao>
            <Botao tema="light" onClick={() => setModal(false)}>
              Cancelar
            </Botao>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default Lista;
